import type { PublicProfile } from "@pax/api-contract/common";
import {
  cn,
  focusHaloFlush,
  HapticSurface,
  panelMotion,
  press,
  Skeleton,
  usePanelPresence,
} from "@pax/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Camera, MoreHorizontal } from "lucide-react";
import { type ReactNode, useCallback, useRef, useState } from "react";
import { BackChevron } from "@/components/BackChevron";
import { LegalLine } from "@/components/LegalLine";
import { AccountAvatar } from "@/features/auth/components/AccountAvatar";
import { ProfileBanner } from "@/features/auth/components/ProfileBanner";
import { ProfileImagesDialog } from "@/features/auth/components/ProfileImagesDialog";
import { useReport } from "@/features/moderation/ReportDialog";
import { profileLink } from "../links";
import { mutualFriendsQuery, profileStatsQuery, relationshipQuery } from "../queries";
import { FriendButton } from "./FriendButton";
import { type ProfileField, ProfileFieldDialog } from "./ProfileFieldDialog";
import { ProfileDetailsPanel, ProfileFriendsPanel } from "./ProfilePanels";
import { ProfileShelves, type Shelf } from "./ProfileShelves";
import { UsernameDialog } from "./UsernameDialog";

/** The banner runs to both edges of the screen. */
const BANNER_SIZES = "100vw";

const column = "app-page-column";

/** The round buttons over the banner, as over a preset's cover. */
const chrome = cn(
  "relative flex size-9 items-center justify-center rounded-full bg-background/70 text-foreground shadow-sm ring-1 ring-foreground/10 sm:size-10",
  panelMotion,
  press,
  focusHaloFlush,
);

/** Round chrome stretched to hold a word, such as Edit profile. */
const wordChrome = "w-auto px-3.5 text-xs font-medium sm:w-auto";

const bannerFade = {
  maskImage: "linear-gradient(to bottom, black 40%, transparent 98%)",
  WebkitMaskImage: "linear-gradient(to bottom, black 40%, transparent 98%)",
};

/** Shown on hover, and always on a touch screen, where there is no hover. */
const revealed =
  "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 [@media(hover:none)]:opacity-100";

/**
 * Someone's page, laid out like a preset's: the banner fading into the page, then who they are,
 * centred, and tabs for what they've made. Counts open what they count, and the rest is in Details
 * behind ⋯. On your own page, the picture, banner, name, username, and bio each open their editor.
 * `viewerId` is the signed-in account looking at it, or null for a guest or nobody.
 */
type ProfileScreenProps = {
  profile: PublicProfile;
  viewerId: string | null;
  /**
   * Your own profile as Settings › Profile shows it, where each part opens its editor. Everywhere
   * else your profile reads like anyone's.
   */
  editing?: boolean;
};

export function ProfileScreen({ profile, viewerId, editing = false }: ProfileScreenProps) {
  const own = viewerId === profile.profileId;
  const editable = own && editing;
  const other = viewerId !== null && !own;
  const stats = useQuery(profileStatsQuery(profile.profileId));
  const relationship = useQuery({ ...relationshipQuery(profile.profileId), enabled: other });
  const mutualCount = relationship.data?.mutualFriendCount ?? 0;
  const report = useReport({ type: "profile", id: profile.profileId });

  const [shelf, setShelf] = useState<Shelf>("presets");
  const shelves = useRef<HTMLDivElement>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const closeDetails = useCallback(() => setDetailsOpen(false), []);
  const details = usePanelPresence(detailsOpen);
  const [friendsOpen, setFriendsOpen] = useState(false);
  const closeFriends = useCallback(() => setFriendsOpen(false), []);
  const friends = usePanelPresence(friendsOpen);
  const [changingImages, setChangingImages] = useState(false);
  const [editingField, setEditingField] = useState<ProfileField | null>(null);
  const [editingUsername, setEditingUsername] = useState(false);

  const openFriends = own || mutualCount > 0 ? () => setFriendsOpen(true) : undefined;

  return (
    <main className={cn("relative isolate flex min-h-full flex-col", editable && "pb-16")}>
      {details.mounted ? (
        <ProfileDetailsPanel
          profile={profile}
          own={editable}
          onEditRegion={() => {
            closeDetails();
            setEditingField("region");
          }}
          onReport={
            own
              ? null
              : () => {
                  closeDetails();
                  report.start();
                }
          }
          leaving={details.leaving}
          onLeft={details.onLeft}
          onClose={closeDetails}
        />
      ) : null}
      {friends.mounted ? (
        <ProfileFriendsPanel
          profile={profile}
          own={own}
          leaving={friends.leaving}
          onLeft={friends.onLeft}
          onClose={closeFriends}
        />
      ) : null}

      <div className="group/banner relative">
        <Editable
          own={editable}
          onEdit={() => setChangingImages(true)}
          label="Change banner"
          className="block w-full"
        >
          <div className="h-52 w-full overflow-hidden sm:h-72" style={bannerFade}>
            {profile.banner ? (
              <ProfileBanner banner={profile.banner} sizes={BANNER_SIZES} />
            ) : (
              <div className="size-full bg-foreground/5" />
            )}
          </div>
        </Editable>
        {/* In Settings, its list is the way out. */}
        {editable ? null : <BackChevron className="absolute top-4 left-4" />}
        <div className="absolute top-4 right-4 flex gap-2">
          {editable ? (
            <button
              type="button"
              onClick={() => setChangingImages(true)}
              className={cn(
                chrome,
                wordChrome,
                "gap-1.5 opacity-0 group-hover/banner:opacity-100 focus-visible:opacity-100 sm:w-auto [@media(hover:none)]:opacity-100",
              )}
            >
              <Camera aria-hidden className="size-3.5" />
              Change banner
              <HapticSurface />
            </button>
          ) : null}
          {/* Your profile as others see it and as you edit it, each a step from the other. */}
          {own ? (
            editable ? (
              <Link {...profileLink(profile)} className={cn(chrome, wordChrome)}>
                View profile
                <HapticSurface />
              </Link>
            ) : (
              <Link
                to="/settings/{-$section}"
                params={{ section: "profile" }}
                className={cn(chrome, wordChrome)}
              >
                Edit profile
                <HapticSurface />
              </Link>
            )
          ) : null}
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            aria-label="Profile details"
            className={chrome}
          >
            <MoreHorizontal className="size-4 sm:size-5" />
            <HapticSurface />
          </button>
        </div>
      </div>

      <div className="relative -mt-14 flex flex-col items-center px-6 text-center">
        <Editable
          own={editable}
          onEdit={() => setChangingImages(true)}
          label="Change picture"
          className="group rounded-full"
        >
          <AccountAvatar
            name={profile.displayName}
            image={profile.avatar}
            size={104}
            className="ring-4 ring-background"
            fallbackClassName="text-2xl"
          />
          {editable ? (
            <span
              className={cn(
                "absolute inset-0 grid place-items-center rounded-full bg-black/45 text-white",
                panelMotion,
                revealed,
              )}
            >
              <Camera aria-hidden className="size-6" />
            </span>
          ) : null}
        </Editable>

        <EditableText
          own={editable}
          onEdit={() => setEditingField("displayName")}
          className="mt-3 px-2"
        >
          <h1 className="text-3xl font-semibold tracking-tight text-balance">
            {profile.displayName}
          </h1>
        </EditableText>
        {profile.username || editable ? (
          <EditableText
            own={editable}
            onEdit={() => setEditingUsername(true)}
            className="mt-0.5 px-2"
          >
            <span className="block text-sm break-all text-muted-foreground">
              {profile.username ? `@${profile.username}` : "Choose a username"}
            </span>
          </EditableText>
        ) : null}
        {profile.bio || editable ? (
          <EditableText
            own={editable}
            onEdit={() => setEditingField("bio")}
            className="mt-3 max-w-md px-2"
          >
            <span
              className={cn(
                "block text-sm whitespace-pre-line text-pretty",
                !profile.bio && "text-muted-foreground",
              )}
            >
              {profile.bio || "Add a bio"}
            </span>
          </EditableText>
        ) : null}

        {other ? (
          <div className="mt-5">
            <FriendButton
              profileId={profile.profileId}
              relationship={relationship.data?.relationship}
            />
          </div>
        ) : null}

        <div className="mt-5 flex gap-4">
          <Count
            value={profile.friendCount.toLocaleString()}
            label="Friends"
            onPress={openFriends}
          />
          <Count
            value={profile.publishedPresetCount.toLocaleString()}
            label="Presets"
            onPress={
              profile.publishedPresetCount > 0
                ? () => {
                    setShelf("presets");
                    shelves.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                : undefined
            }
          />
          <Count
            value={
              stats.isError
                ? "–"
                : stats.data
                  ? stats.data.roundsPlayed.toLocaleString()
                  : undefined
            }
            label="Rounds"
          />
        </div>

        {other && mutualCount > 0 ? (
          <button
            type="button"
            onClick={() => setFriendsOpen(true)}
            className={cn(
              "mt-3 rounded-full px-2 py-1 hover:bg-foreground/5",
              panelMotion,
              focusHaloFlush,
            )}
          >
            <MutualFriends profileId={profile.profileId} count={mutualCount} />
          </button>
        ) : null}
      </div>

      <div className={`${column} mt-10`}>
        <ProfileShelves
          ref={shelves}
          profile={profile}
          own={own}
          shelf={shelf}
          onShelfChange={setShelf}
        />
      </div>

      {report.dialog}
      {editable ? (
        <>
          <ProfileImagesDialog
            profile={profile}
            open={changingImages}
            onOpenChange={setChangingImages}
          />
          <ProfileFieldDialog
            profile={profile}
            field={editingField}
            onOpenChange={open => {
              if (!open) setEditingField(null);
            }}
          />
          <UsernameDialog
            profile={profile}
            open={editingUsername}
            onOpenChange={setEditingUsername}
          />
        </>
      ) : null}
      {/* Settings has its own way to the policies, so the line closes only the page others see. */}
      {editable ? null : <LegalLine />}
    </main>
  );
}

/** On your own profile, the picture or banner, which opens the image dialog when clicked. */
function Editable({
  own,
  onEdit,
  label,
  className,
  children,
}: {
  own: boolean;
  onEdit: () => void;
  label: string;
  className?: string;
  children: ReactNode;
}) {
  if (!own) return <div className={cn("relative", className)}>{children}</div>;
  return (
    <button
      type="button"
      onClick={onEdit}
      aria-label={label}
      className={cn("relative outline-none", focusHaloFlush, className)}
    >
      {children}
    </button>
  );
}

/** On your own profile, text that opens its editor when clicked. */
function EditableText({
  own,
  onEdit,
  className,
  children,
}: {
  own: boolean;
  onEdit: () => void;
  className?: string;
  children: ReactNode;
}) {
  if (!own) return <div className={className}>{children}</div>;
  return (
    <button
      type="button"
      onClick={onEdit}
      className={cn(
        "relative block rounded-lg hover:bg-foreground/5",
        panelMotion,
        focusHaloFlush,
        className,
      )}
    >
      {children}
    </button>
  );
}

/** A figure, which opens what it counts when it can. */
function Count({
  value,
  label,
  onPress,
}: {
  value: string | undefined;
  label: string;
  onPress?: () => void;
}) {
  const body = (
    <>
      <span className="text-lg leading-tight font-semibold tabular-nums">
        {value ?? <Skeleton className="my-0.5 h-5 w-10" />}
      </span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </>
  );
  if (!onPress) {
    return <div className="flex flex-col items-center gap-0.5 px-3 py-1.5">{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={onPress}
      className={cn(
        "relative flex flex-col items-center gap-0.5 rounded-xl px-3 py-1.5 hover:bg-foreground/5",
        panelMotion,
        press,
        focusHaloFlush,
      )}
    >
      {body}
      <HapticSurface />
    </button>
  );
}

/** Up to three of the friends you share, overlapping, and how many there are. */
function MutualFriends({ profileId, count }: { profileId: string; count: number }) {
  const { data } = useQuery(mutualFriendsQuery(profileId));
  return (
    <span className="flex items-center gap-2 text-xs text-muted-foreground">
      {data ? (
        <span className="flex -space-x-1.5">
          {data.profiles.slice(0, 3).map(friend => (
            <AccountAvatar
              key={friend.profileId}
              name={friend.displayName}
              image={friend.avatar}
              size={20}
              className="ring-2 ring-background"
              fallbackClassName="text-[8px]"
            />
          ))}
        </span>
      ) : null}
      {count === 1 ? "1 mutual friend" : `${count.toLocaleString()} mutual friends`}
    </span>
  );
}

/** The profile page while its profile loads. */
export function ProfileSkeleton() {
  return (
    <main aria-busy className="min-h-full pb-16">
      <div className="h-52 w-full bg-foreground/5 sm:h-72" style={bannerFade} />
      <div className="-mt-14 flex flex-col items-center px-6">
        <Skeleton className="size-26 rounded-full ring-4 ring-background" />
        <Skeleton className="mt-4 h-8 w-56" />
        <Skeleton className="mt-2 h-4 w-28" />
        <Skeleton className="mt-6 h-12 w-64" />
      </div>
    </main>
  );
}
